/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react'
import Tooltip from '@mui/material/Tooltip'
import { useTranslation } from 'react-i18next'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import { Box, DialogContent, DialogTitle, useMediaQuery, useTheme } from '@mui/material'
import { Edit, Delete } from '@mui/icons-material'

import ContainedButton from 'shared/components/Buttons/ContainedButton'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import GrayButton from 'shared/components/Buttons/GrayButton'
import useLogin from 'shared/store/useLogin'
import { noInformationsWholesales, UserRoles } from 'shared/consts'
import VivaldiIcon from 'assets/images/browserIcons/vivaldi.png'
import FirefoxIcon from 'assets/images/browserIcons/firefox.png'
import ChromeIcon from 'assets/images/browserIcons/chrome.png'
import OperaIcon from 'assets/images/browserIcons/opera.png'
import BraveIcon from 'assets/images/browserIcons/brave.png'
import EdgeIcon from 'assets/images/browserIcons/edge.png'
import CustomDialog from 'shared/components/CustomDialog'

import { ButtonContainer } from './styles'

const extensions = [
   {
      icon: ChromeIcon,
      link: 'https://chromewebstore.google.com/detail/euromaster/jbleagdegjmfcadakjlommmgndijpobb',
   },
   {
      icon: FirefoxIcon,
      link: 'https://addons.mozilla.org/pl/firefox/addon/euromaster/',
   },
   {
      icon: EdgeIcon,
      link: 'https://chromewebstore.google.com/detail/euromaster/jbleagdegjmfcadakjlommmgndijpobb',
   },
   {
      icon: OperaIcon,
      link: 'https://chromewebstore.google.com/detail/euromaster/jbleagdegjmfcadakjlommmgndijpobb',
   },
   {
      icon: VivaldiIcon,
      link: 'https://chromewebstore.google.com/detail/euromaster/jbleagdegjmfcadakjlommmgndijpobb',
   },
   {
      icon: BraveIcon,
      link: 'https://chromewebstore.google.com/detail/euromaster/jbleagdegjmfcadakjlommmgndijpobb',
   },
]

interface Props {
   onBuy: () => void
   onAddToCart: () => void
   status: CooperatingWholesalerStatus
   totalAvailability: Nullable<number>
   errorMessage: Nullable<string>
   isLoading: boolean
   wholesaleUuid: string
   wholesaleIndex: string
   productIndex: string
   netPrice: Nullable<number>
   onReportMissingProduct: () => void
   onEditWholesaler: () => void
   onRemoveWholesaler: () => void
}

export default function Actions({
   onAddToCart,
   onBuy,
   onEditWholesaler,
   onReportMissingProduct,
   onRemoveWholesaler,
   wholesaleUuid,
   productIndex,
   status,
   netPrice,
   errorMessage,
   wholesaleIndex,
   totalAvailability,
   isLoading,
}: Props) {
   const { t } = useTranslation('PRODUCT_DETAILS')
   const { user } = useLogin()
   const theme = useTheme()
   const [openExtensionModal, setOpenExtensionModal] = useState(false)
   const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
   const [extensionInstalled, setExtensionInstalled] = useState(false)

   useEffect(() => {
      const detectBrowser = () => {
         const ua = navigator.userAgent

         if (/OPR|Opera/i.test(ua)) return 'Opera'
         if (/Edg/i.test(ua)) return 'Edge'
         if (/Chrome/i.test(ua) && !/Edg/i.test(ua) && !/OPR/i.test(ua)) return 'Chrome'
         if (/Firefox/i.test(ua)) return 'Firefox'
         if (/Safari/i.test(ua) && !/Chrome/i.test(ua) && !/OPR/i.test(ua)) return 'Safari'

         return 'Unknown'
      }

      const handler = (event: any) => {
         if (event.data && event.data.type === 'EXTENSION_INSTALLED') {
            setExtensionInstalled(true)
         }
      }
      if (detectBrowser() === 'Firefox') {
         setExtensionInstalled(true)
      } else {
         window.addEventListener('message', handler)

         window.postMessage({ type: 'CHECK_EXTENSION' }, '*')
      }
      return () => window.removeEventListener('message', handler)
   }, [])

   function renderEditButton() {
      if (user.roles.includes(UserRoles.DATA_MANAGER)) {
         return (
            <Box
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexDirection: { lg: 'row', md: 'row', xs: 'column' },
               }}
            >
               <Box
                  sx={{
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'space-between',
                  }}
               >
                  <IconButton onClick={onEditWholesaler}>
                     <Edit />
                  </IconButton>
                  <IconButton onClick={onRemoveWholesaler}>
                     <Delete color="error" />
                  </IconButton>
               </Box>
               {wholesaleIndex}
            </Box>
         )
      }
      return null
   }

   function renderConnectionProblemContent() {
      if (errorMessage && !errorMessage.includes('NOT_HANDLED')) {
         return (
            <>
               <Typography
                  sx={{
                     fontWeight: 'bold',
                     color: 'red',
                     border: '1px solid red',
                     p: 1,
                     maxWidth: isMobile ? '100px' : 'none',
                     borderRadius: '5px',
                  }}
                  variant="body1"
               >
                  {errorMessage}
               </Typography>
               {renderEditButton()}
            </>
         )
      }
      return (
         <>
            <Typography
               sx={{
                  fontWeight: 'bold',
                  color: 'red',
                  border: '1px solid red',
                  p: 1,
                  borderRadius: '5px',
                  maxWidth: isMobile ? '100px' : 'none',
               }}
               variant="body1"
            >
               {t('tables.wholesaleReconnectFailed')}
            </Typography>
            {renderEditButton()}
         </>
      )
   }

   if (
      status === 'ONLINE_CONNECTION_PROBLEM' ||
      status === 'PRODUCT_NOT_EXIST_IN_WHOLESALE' ||
      (status === 'OK_ONLINE_AVAILABILITY' && !netPrice)
   ) {
      return (
         <ButtonContainer sx={{ alignItems: 'center', justifyContent: 'center' }}>
            {renderConnectionProblemContent()}
         </ButtonContainer>
      )
   }
   if (
      (!totalAvailability || totalAvailability < 0) &&
      !noInformationsWholesales[wholesaleUuid] &&
      status === 'OK_ONLINE_AVAILABILITY'
   ) {
      return (
         <ButtonContainer sx={{ justifyContent: 'center', alignItems: 'center' }}>
            <Tooltip
               placement="right"
               disableHoverListener={status !== 'OK_ONLINE_AVAILABILITY'}
               title={t('tables.sendReportTooltip') || ''}
            >
               <span>
                  <GrayButton
                     sx={{
                        maxWidth: isMobile ? '100px' : 'none',
                     }}
                     fullWidth={isMobile}
                     variant="outlined"
                     type="button"
                     onClick={onReportMissingProduct}
                     disabled={
                        status !== 'OK_ONLINE_AVAILABILITY' ||
                        isLoading ||
                        user.roles.includes(UserRoles.DEMO) ||
                        user.roles.includes(UserRoles.DEMO_SUBSCRIPTION)
                     }
                  >
                     {t('tables.reportButton')}
                  </GrayButton>
               </span>
            </Tooltip>
            {renderEditButton()}
         </ButtonContainer>
      )
   }
   return (
      <Box>
         <ButtonContainer
            sx={{
               maxWidth: user.roles.includes(UserRoles.DATA_MANAGER) ? '400px' : 'none',
               ml: user.roles.includes(UserRoles.DATA_MANAGER) ? 'auto' : '0',
               mr: user.roles.includes(UserRoles.DATA_MANAGER) ? 'auto' : '0',
               display: 'flex',
               justifyContent: user.roles.includes(UserRoles.DATA_MANAGER) ? 'normal' : 'center',
               alignItems: 'center',
            }}
         >
            <ContainedButton
               sx={{
                  maxWidth: isMobile ? '100px' : 'none',
               }}
               fullWidth={isMobile}
               variant="contained"
               onClick={onBuy}
               disabled={
                  status !== 'OK_ONLINE_AVAILABILITY' ||
                  isLoading ||
                  user?.roles.includes(UserRoles.SALES_REP) ||
                  !totalAvailability ||
                  !!noInformationsWholesales[wholesaleUuid]
               }
            >
               {t('tables.buyNowButton')}
            </ContainedButton>
            <OutlinedButton
               sx={{
                  maxWidth: isMobile ? '100px' : 'none',
               }}
               fullWidth={isMobile}
               variant="outlined"
               onClick={onAddToCart}
               disabled={
                  status !== 'OK_ONLINE_AVAILABILITY' ||
                  isLoading ||
                  !totalAvailability ||
                  !!noInformationsWholesales[wholesaleUuid]
               }
            >
               {isMobile ? t('tables.addToCartButtonShort') : t('tables.addToCartButton')}
            </OutlinedButton>
            {renderEditButton()}
         </ButtonContainer>
         {noInformationsWholesales[wholesaleUuid] && status !== 'OK_ONLINE_AVAILABILITY' && (
            <Box
               sx={{
                  maxWidth: user.roles.includes(UserRoles.DATA_MANAGER) ? '400px' : 'none',
                  ml: user.roles.includes(UserRoles.DATA_MANAGER) ? 'auto' : '0',
                  mr: user.roles.includes(UserRoles.DATA_MANAGER) ? 'auto' : '0',
                  display: 'flex',
                  justifyContent: user.roles.includes(UserRoles.DATA_MANAGER) ? 'normal' : 'center',
                  alignItems: 'center',
                  mt: '8px',
               }}
            >
               {extensionInstalled ? (
                  <ContainedButton
                     // temp disabled
                     variant="contained"
                     disabled
                     fullWidth
                     sx={{
                        maxWidth: isMobile ? '100px' : '240px',
                     }}
                     onClick={() => {
                        const win = window.open('about:blank', '_blank')

                        if (!win) return
                        win.name = JSON.stringify({ check: true })
                        win.location.href = `https://pl.e-cat.intercars.eu/pl/Pełna-oferta/c/tecdoc?q=${
                           wholesaleIndex ?? productIndex
                        }%3Adefault-m%3AbranchAvailability%3AALL&sort=default-m`
                     }}
                  >
                     {t('tables.checkAvailability')}
                  </ContainedButton>
               ) : (
                  <ContainedButton
                     fullWidth
                     sx={{
                        maxWidth: isMobile ? '100px' : '240px',
                     }}
                     onClick={() => setOpenExtensionModal(true)}
                  >
                     {t('tables.downloadExtension')}
                  </ContainedButton>
               )}
            </Box>
         )}
         <CustomDialog
            isLoading={false}
            fullScreen={isMobile}
            onClose={() => setOpenExtensionModal(false)}
            open={openExtensionModal}
            content={
               <>
                  <DialogTitle>{t('modals.extensionModalTitle')}</DialogTitle>
                  <DialogContent>
                     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="h6">{t('modals.extensionModalInstall')}</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                           {extensions.map((item) => (
                              <IconButton key={item.icon} onClick={() => window.open(item.link, '_blank')}>
                                 <img height={30} width="auto" src={item.icon} alt="chrome" />
                              </IconButton>
                           ))}
                        </Box>
                        <Typography variant="h6">{t('modals.extensionModalInstalled')}</Typography>
                        <ContainedButton
                           sx={{ alignSelf: 'center' }}
                           onClick={() => {
                              const win = window.open('about:blank', '_blank')

                              if (!win) return
                              win.name = JSON.stringify({ check: true })
                              win.location.href = `https://pl.e-cat.intercars.eu/pl/Pełna-oferta/c/tecdoc?q=${
                                 wholesaleIndex ?? productIndex
                              }%3Adefault-m%3AbranchAvailability%3AALL&sort=default-m`
                              setOpenExtensionModal(false)
                           }}
                        >
                           {t('tables.checkAvailability')}
                        </ContainedButton>
                     </Box>
                  </DialogContent>
               </>
            }
         />
      </Box>
   )
}
